import type { SearchPageBody, SearchPageParams } from "@apptus/esales-api";
import elevateClient from "@server/elevate";
import type { Loop54Content } from "@server/utils/parseLoop/types";

import type { MarketConfiguration } from "@/config/markets/const";
import { SAMPLE_FLAG } from "@/config/products";

import mapElevateFacets from "./mapElevateFacets";
import mapElevateProductGroups from "./mapElevateProductGroups";
import mapCustomAttributes from "./mapCustomAttributes";

/**
 * Excludes fabric samples from search results so brand queries (e.g.
 * "nordicknots", "no ga") don't surface fabric samples. Samples are flagged
 * with `is_sample` at either the product- or variant-level, so we exclude on
 * both flag attributes (NOGA-2164).
 */
const SAMPLE_EXCLUSION_RULE = `rule excl custom.productFlags { "${SAMPLE_FLAG}" } excl custom.variantFlags { "${SAMPLE_FLAG}" }`;

export default async function getSearchPage(
  market: MarketConfiguration,
  input: SearchPageParams,
  config?: SearchPageBody,
): Promise<Loop54Content> {
  const response = await elevateClient(market).query.searchPage(
    {
      viewId: "production",
      notify:
        process.env.NEXT_PUBLIC_ENVIRONMENT === "prod" &&
        typeof window !== "undefined",
      limit: 60,
      skip: 0,
      presentCustom: mapCustomAttributes(["statuses"], market),
      presentPrices: [
        `NG_${market.code.toLowerCase()}_best`,
        `NG_${market.code.toLowerCase()}_RRP`,
        `NG_${market.code.toLowerCase()}_pricematch`,
        `NG_${market.code.toLowerCase()}_campaign`,
      ],
      priceId: `NG_${market.code.toLowerCase()}_best`,
      ...input,
    },
    {
      ...config,
      primaryList: {
        include: true,
        ...config?.primaryList,
        productRules: config?.primaryList?.productRules ?? SAMPLE_EXCLUSION_RULE,
      },
    },
  );

  if (!response.primaryList)
    return {
      items: [],
      filter: [],
      count: 0,
    };

  const { productGroups, facets, totalHits: count } = response.primaryList;

  const items = mapElevateProductGroups({
    productGroups,
    marketConfig: market,
  });
  const filter = mapElevateFacets(facets);

  return { items, filter, count };
}
